import {SidebarAdvertiser} from '@/components/sidebar/SidebarAdvertiser.tsx'
import {AdGroupAdvForm} from '@/pages/ad-group/AdGroupAdvForm'
import {AdGroupAdvList} from '@/pages/ad-group/AdGroupAdvList'
import {ContextualTargetingListAdvertiser} from '@/pages/contextual-targeting/ContextualTargetingListAdvertiser.tsx';
import {BreadcrumbAdvertiser} from '@/pages/breadcrumb/BreadcrumbAdvertiser.tsx'
import {CampaignAdvForm} from '@/pages/campaign/CampaignAdvForm'
import {CampaignAdvList} from '@/pages/campaign/CampaignAdvList'
import {CreativeAdvForm} from '@/pages/creative/CreativeAdvForm'
import {CreativeAdvList} from '@/pages/creative/CreativeAdvList'
import {FilterListFormAdv} from '@/pages/filter-list/FilterListFormAdv.tsx'
import {FilterListListAdv} from '@/pages/filter-list/FilterListListAdv.tsx'
import * as routes from '@/pages/routes'
import {TrackerAdvForm} from '@/pages/tracker/TrackerAdvForm.tsx'
import {TrackerAdvList} from '@/pages/tracker/TrackerAdvList.tsx'
import {TransactionAdvList} from '@/pages/transactions/TransactionAdvList.tsx'
import {Layout} from 'antd'
import {Content, Header} from 'antd/lib/layout/layout'
import {AdvertiserBalanceBean, AgencyBean, ReportRequestParametersObjectObject} from 'common/api/api'
import {balanceApi} from 'common/api/entitiesApi'
import {ErrorModal} from 'common/components/ErrorModal'
import {CommonRoutes, normalizeSelectedMenuKey} from 'common/context/commonRoutes.ts'
import {useUserContext} from 'common/context/UserContext'
import {Dashboard} from 'common/pages/dashboard/Dashboard'
import {FormMode, isAuthenticated, setLoggedUserProps} from 'common/util/Util'
import * as React from 'react'
import {useEffect, useState} from 'react'
import {Navigate, Route, Routes, useLocation} from 'react-router-dom'

import Profile from './account/Profile'
import {FilterListGlobalListAdvertiser} from '@/pages/filter-list-global/FilterListGlobalListAdvertiser.tsx'
import {FilterListGlobalFormAdvertiser} from '@/pages/filter-list-global/FilterListGlobalFormAdvertiser.tsx'
import {ReportFormAdvertiser} from '@/pages/report/ReportFormAdvertiser.tsx'
import {SavedReportListAdvertiser} from '@/pages/report/SavedReportListAdvertiser.tsx'
import {RetargetingListFormAdvertiser} from '@/pages/retargeting-list/RetargetingListFormAdvertiser.tsx'
import {RetargetingListListAdvertiser} from '@/pages/retargeting-list/RetargetingListListAdvertiser.tsx'
import {TrackerEventListAdvertiser} from '@/pages/tracker-event-stats/TrackerEventListAdvertiser.tsx'

const { FeatureAccessEnum } = AgencyBean

export const AppAdvertiser: React.FC = () => {
  const { user, setUser, colorPalette, featureAccess } = useUserContext()
  const [selectedMenuKeys, setSelectedMenuKeys] = useState([''])
  const [collapsed, setCollapsed] = useState(false)
  const [balance, setBalance] = useState<AdvertiserBalanceBean>()

  const location = useLocation()
  const fetchBalance = React.useCallback(() => {
    if (!user?.advertiserId) return
    balanceApi.balance(user.advertiserId).then((res) => setBalance(res.data))
  }, [user?.advertiserId])

  useEffect(() => {
    if (isAuthenticated()) {
      setLoggedUserProps(setUser)
    }
  }, [setUser])

  useEffect(() => {
    fetchBalance()
    const intervalId = setInterval(fetchBalance, 60_000)

    return () => {
      clearInterval(intervalId)
    }
  }, [fetchBalance])

  useEffect(() => {
    setSelectedMenuKeys([normalizeSelectedMenuKey(location.pathname)])
  }, [location])

  const hasFeature = (feature: AgencyBean.FeatureAccessEnum) => {
    if (!featureAccess || featureAccess.length === 0) {
      return true
    }
    return featureAccess.includes(feature)
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" />
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <SidebarAdvertiser selectedKeys={selectedMenuKeys} collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout>
        <Header
          style={{
            padding: 0,
            backgroundColor: colorPalette['--color-grey-4'] ?? '#0D1A43',
            display: 'flex',
            alignItems: 'center',
            height: 60,
          }}
        >
          <BreadcrumbAdvertiser
            collapsed={collapsed}
            balance={balance}
            onBalanceRefresh={fetchBalance}
            stripePaymentsEnabled={balance?.stripePaymentsEnabled ?? false}
          />
        </Header>
        <ErrorModal />
        <Content style={{ margin: 16 }}>
          <Routes>
            <Route path="/" element={<Navigate to={CommonRoutes.DASHBOARD} replace />} />

            {hasFeature(FeatureAccessEnum.Advertiser) && (
              <>
                <Route path={CommonRoutes.DASHBOARD} element={<Dashboard agency={false} />} />
                <Route path={CommonRoutes.CAMPAIGN_CREATE} element={<CampaignAdvForm mode={FormMode.create} />} />
                <Route path={CommonRoutes.CAMPAIGN_EDIT} element={<CampaignAdvForm mode={FormMode.edit} />} />
                <Route path={CommonRoutes.CAMPAIGN_COPY} element={<CampaignAdvForm mode={FormMode.copy} />} />
                <Route path={CommonRoutes.CAMPAIGN} element={<CampaignAdvList />} />

                <Route path={CommonRoutes.AD_GROUP_CREATE} element={<AdGroupAdvForm mode={FormMode.create} />} />
                <Route path={CommonRoutes.AD_GROUP_COPY} element={<AdGroupAdvForm mode={FormMode.copy} />} />
                <Route path={CommonRoutes.AD_GROUP_EDIT} element={<AdGroupAdvForm mode={FormMode.edit} />} />
                <Route path={CommonRoutes.AD_GROUP} element={<AdGroupAdvList />} />

                <Route path={CommonRoutes.CREATIVE_CREATE} element={<CreativeAdvForm mode={FormMode.create} />} />
                <Route path={CommonRoutes.CREATIVE_COPY} element={<CreativeAdvForm mode={FormMode.copy} />} />
                <Route path={CommonRoutes.CREATIVE_EDIT} element={<CreativeAdvForm mode={FormMode.edit} />} />
                <Route path={CommonRoutes.CREATIVE} element={<CreativeAdvList />} />

                <Route
                  path={CommonRoutes.REPORT}
                  element={<SavedReportListAdvertiser reportType={ReportRequestParametersObjectObject.TypeEnum.Stats} />}
                />
                <Route path={CommonRoutes.REPORT_BUILDER} element={<ReportFormAdvertiser mode={FormMode.create} />} />
                <Route path={CommonRoutes.REPORT_COPY} element={<ReportFormAdvertiser mode={FormMode.copy} />} />
                <Route path={CommonRoutes.REPORT_EDIT} element={<ReportFormAdvertiser mode={FormMode.edit} />} />
                <Route path={CommonRoutes.REPORT_OPEN} element={<ReportFormAdvertiser mode={FormMode.edit} autoLoad={true} />} />

                <Route path={routes.BILLING_HISTORY} element={<TransactionAdvList balance={balance} />} />

                <Route path={CommonRoutes.TRACKER_CREATE} element={<TrackerAdvForm mode={FormMode.create} />} />
                <Route path={CommonRoutes.TRACKER_EDIT} element={<TrackerAdvForm mode={FormMode.edit} />} />
                <Route path={CommonRoutes.TRACKER} element={<TrackerAdvList />} />

                <Route
                  path={CommonRoutes.TRACKER_EVENT_REPORT}
                  element={
                    <SavedReportListAdvertiser
                      reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker}
                      createRoute={CommonRoutes.TRACKER_EVENT_REPORT_CREATE}
                      reportRouteBase={CommonRoutes.TRACKER_EVENT_REPORT}
                      title="Tracker Reports"
                    />
                  }
                />
                <Route path={CommonRoutes.TRACKER_EVENT_REPORT_CREATE} element={<TrackerEventListAdvertiser />} />
                <Route
                  path={CommonRoutes.TRACKER_EVENT_REPORT_EDIT}
                  element={<ReportFormAdvertiser mode={FormMode.edit} reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker} savedReportBaseRoute={CommonRoutes.TRACKER_EVENT_REPORT} />}
                />
                <Route
                  path={CommonRoutes.TRACKER_EVENT_REPORT_COPY}
                  element={<ReportFormAdvertiser mode={FormMode.copy} reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker} savedReportBaseRoute={CommonRoutes.TRACKER_EVENT_REPORT} />}
                />

                <Route path={CommonRoutes.FILTER_LIST} element={<FilterListListAdv />} />
                <Route path={CommonRoutes.FILTER_LIST_CREATE} element={<FilterListFormAdv mode={FormMode.create} />} />
                <Route path={CommonRoutes.FILTER_LIST_EDIT} element={<FilterListFormAdv mode={FormMode.edit} />} />

                <Route path={CommonRoutes.FILTER_LIST_GLOBAL} element={<FilterListGlobalListAdvertiser />} />
                <Route path={CommonRoutes.FILTER_LIST_GLOBAL_CREATE} element={<FilterListGlobalFormAdvertiser mode={FormMode.create} />} />
                <Route path={CommonRoutes.FILTER_LIST_GLOBAL_EDIT} element={<FilterListGlobalFormAdvertiser mode={FormMode.edit} />} />

                <Route path={CommonRoutes.RETARGETING_LIST} element={<RetargetingListListAdvertiser />} />
                <Route path={CommonRoutes.RETARGETING_LIST_OPEN} element={<RetargetingListFormAdvertiser />} />

                <Route path={CommonRoutes.CONTEXTUAL_TARGETING} element={<ContextualTargetingListAdvertiser />} />
              </>
            )}

            <Route path={CommonRoutes.PROFILE_EDIT} element={<Profile />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  )
}
